import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";

export interface BrainScope {
  genieId: string;
  genieName?: string;
}

interface BrainContextValue {
  isOpen: boolean;
  openBrain: (initialMessage?: string, scope?: BrainScope) => void;
  closeBrain: () => void;
  setIsOpen: (open: boolean) => void;
  pendingMessage: string | null;
  consumePendingMessage: () => string | null;
  scope: BrainScope | null;
  setScope: (scope: BrainScope | null) => void;
}

const BrainContext = createContext<BrainContextValue | null>(null);

// Routes whose `:genieId` param should auto-scope Your Genie to that genie.
const GENIE_SCOPED_ROUTES = [
  "/genie-hub/my-genies/:genieId",
  "/genie-hub/knowledge-base/:genieId",
  "/genie-hub/activation/genie/:agentId",
];

const matchGenieIdFromPath = (pathname: string): string | null => {
  for (const pattern of GENIE_SCOPED_ROUTES) {
    const match = matchPath(pattern, pathname);
    if (match) {
      return match.params.genieId ?? match.params.agentId ?? null;
    }
  }
  return null;
};

export const BrainProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [scope, setScopeState] = useState<BrainScope | null>(null);
  const [scopeManuallySet, setScopeManuallySet] = useState(false);

  const location = useLocation();

  // Auto-scope from URL when the route changes — unless the user has
  // manually picked a scope in this session (then we respect their choice).
  useEffect(() => {
    if (scopeManuallySet) return;
    const detectedGenieId = matchGenieIdFromPath(location.pathname);
    if (detectedGenieId) {
      setScopeState((current) =>
        current?.genieId === detectedGenieId ? current : { genieId: detectedGenieId },
      );
    } else {
      setScopeState(null);
    }
  }, [location.pathname, scopeManuallySet]);

  const openBrain = useCallback((initialMessage?: string, nextScope?: BrainScope) => {
    if (initialMessage) {
      setPendingMessage(initialMessage);
    }
    if (nextScope) {
      setScopeState(nextScope);
    }
    setIsOpen(true);
  }, []);

  const closeBrain = useCallback(() => {
    setIsOpen(false);
  }, []);

  const consumePendingMessage = useCallback(() => {
    const msg = pendingMessage;
    setPendingMessage(null);
    return msg;
  }, [pendingMessage]);

  const setScope = useCallback((next: BrainScope | null) => {
    setScopeManuallySet(true);
    setScopeState(next);
  }, []);

  return (
    <BrainContext.Provider
      value={{
        isOpen,
        openBrain,
        closeBrain,
        setIsOpen,
        pendingMessage,
        consumePendingMessage,
        scope,
        setScope,
      }}
    >
      {children}
    </BrainContext.Provider>
  );
};

export const useBrain = () => {
  const ctx = useContext(BrainContext);
  if (!ctx) throw new Error("useBrain must be used within BrainProvider");
  return ctx;
};
